import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { FaInstagram, FaYoutube, FaFacebook, FaTiktok } from 'react-icons/fa';
import {
  MapPinIcon,
  EnvelopeIcon,
  ArrowUpIcon,
} from '@heroicons/react/24/outline';
import Logo from '../common/Logo';

const ImprovedFooter = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  // Back to top visibility
  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const socialLinks = [
    {
      name: 'Instagram',
      icon: FaInstagram,
      href: 'https://www.instagram.com/rumonrahmanmd/',
      color: 'hover:text-pink-400',
    },
    {
      name: 'TikTok',
      icon: FaTiktok,
      href: 'https://www.tiktok.com/@rumonrehman?lang=en',
      color: 'hover:text-white',
    },
    {
      name: 'YouTube',
      icon: FaYoutube,
      href: 'https://www.youtube.com/@mermaidsofthemoment',
      color: 'hover:text-red-400',
    },
    {
      name: 'Facebook',
      icon: FaFacebook,
      href: 'https://www.facebook.com/profile.php?id=100092539025966',
      color: 'hover:text-blue-500',
    },
  ];

  const quickLinks = [
    { name: 'Home', href: '/' },
    { name: 'Blog', href: '/blog' },
    { name: 'Gallery', href: '/gallery' },
    { name: 'About', href: '/about' },
    { name: 'Contact', href: '/contact' },
  ];

  const categories = [
    { name: 'Adventure Travel', href: '/blog?category=adventure-travel' },
    { name: 'Beach Destinations', href: '/blog?category=beach-destinations' },
    {
      name: 'Cultural Experiences',
      href: '/blog?category=cultural-experiences',
    },
    { name: 'Travel Tips', href: '/blog?category=travel-tips' },
    { name: 'Tech', href: '/blog?category=tech' },
    { name: 'Lifestyle', href: '/blog?category=lifestyle' },
  ];

  return (
    <footer className="relative overflow-hidden rounded-t-3xl shadow-[inset_0_1px_0_rgba(255,255,255,0.06)]">
      {/* Main Footer Content */}
      <div className="bg-[#0B1220] text-slate-200">
        <div className="container-max py-8">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 lg:gap-12">
            {/* Brand Section */}
            <div className="lg:col-span-2">
              <div className="mb-8">
                <Logo
                  size="lg"
                  showText={true}
                  linkTo="/"
                  textColor="text-white"
                  className="mb-6"
                />
                <p className="text-base md:text-lg text-slate-400 leading-relaxed max-w-lg">
                  Discover the world's most incredible destinations through our
                  eyes. Join us on unforgettable adventures and let your
                  wanderlust guide you to magical places.
                </p>
              </div>

              {/* Social Media Links */}
              <div className="space-y-6">
                <h4 className="text-lg font-semibold text-cyan-300 flex items-center">
                  🌐 Follow Our Journey
                </h4>
                <div className="flex items-center space-x-3">
                  {socialLinks.map(social => {
                    const IconComponent = social.icon;
                    return (
                      <a
                        key={social.name}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`p-2.5 bg-white/5 backdrop-blur-md rounded-lg ${social.color} transition-all duration-300 hover:bg-white/10 hover:shadow-md group border border-white/10`}
                        aria-label={social.name}
                      >
                        <IconComponent className="w-5 h-5 transition-all duration-300 group-hover:scale-110" />
                      </a>
                    );
                  })}
                </div>
                <p className="text-cyan-300/80 text-sm">
                  📱 Get daily travel inspiration • 🎥 Behind-the-scenes content
                  • 💬 Connect with fellow travelers
                </p>
              </div>
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-heading font-semibold mb-4 text-blue-300">
                🚀 Quick Links
              </h4>
              <ul className="space-y-2.5">
                {quickLinks.map(link => (
                  <li key={link.name}>
                    <Link
                      to={link.href}
                      className="text-slate-400 hover:text-cyan-300 transition-all duration-200 inline-flex items-center gap-2"
                    >
                      <span className="text-cyan-300">•</span>
                      <span>{link.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>

            {/* Categories */}
            <div>
              <h4 className="text-lg font-heading font-semibold mb-4 text-orange-300">
                🗺️ Explore
              </h4>
              <ul className="space-y-2.5">
                {categories.map(category => (
                  <li key={category.name}>
                    <Link
                      to={category.href}
                      className="text-slate-400 hover:text-orange-300 transition-all duration-200 inline-flex items-center gap-2"
                    >
                      <span className="text-orange-300">•</span>
                      <span>{category.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </div>

          {/* Contact Info */}
          <div className="mt-10 pt-6 border-t border-white/10">
            <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
              <div className="flex flex-col sm:flex-row items-center space-y-2 sm:space-y-0 sm:space-x-6 text-slate-300/90">
                <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                  <MapPinIcon className="w-4 h-4 mr-2 text-cyan-300" />
                  <span>Based in Paradise</span>
                </div>
                <div className="flex items-center bg-white/5 backdrop-blur-sm px-3 py-1.5 rounded-lg">
                  <EnvelopeIcon className="w-4 h-4 mr-2 text-cyan-300" />
                  <span>rumon1@gmail.com</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10 bg-[#0A0F1A]">
        <div className="container-max py-6">
          <div className="flex flex-col lg:flex-row justify-between items-center space-y-4 lg:space-y-0">
            <p className="text-slate-400 flex items-center gap-2">
              © {new Date().getFullYear()} Mermaids Of The Moment
              <span className="opacity-60">•</span>
              Made with <span className="text-pink-400">❤</span> for fellow
              travelers
            </p>
            <div className="flex items-center space-x-6 text-sm">
              <Link
                to="/privacy"
                className="text-slate-400 hover:text-white transition-colors hover:underline"
              >
                Privacy Policy
              </Link>
              <Link
                to="/terms"
                className="text-slate-400 hover:text-white transition-colors hover:underline"
              >
                Terms of Service
              </Link>
              <Link
                to="/sitemap"
                className="text-slate-400 hover:text-white transition-colors hover:underline"
              >
                Sitemap
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Back to Top Button */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="fixed bottom-8 right-8 z-50 p-4 bg-blue-600 text-white rounded-full shadow-xl hover:shadow-blue-500/40 transition-all duration-200 hover:scale-105 focus:outline-none focus:ring-4 focus:ring-blue-300/40"
          aria-label="Back to top"
        >
          <ArrowUpIcon className="h-6 w-6" />
        </button>
      )}
    </footer>
  );
};

export default ImprovedFooter;
